import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./useAuth";

export interface Highlight {
  id: string;
  bookId: number;
  bookName: string;
  chapter: number;
  verseNum: number;
  verseText: string;
  color: string;
  translation: string;
  createdAt: string;
}

const STORAGE_KEY = "applesmp_highlights";

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getAllHighlights(): (Highlight & { userId: string })[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function saveAllHighlights(data: (Highlight & { userId: string })[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function useHighlights(bookId?: number, chapter?: number) {
  const { user } = useAuth();
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [loading] = useState(false);

  const fetchHighlights = useCallback(() => {
    if (!user) {
      setHighlights([]);
      return;
    }
    let data = getAllHighlights().filter((h) => h.userId === user.id);
    if (bookId !== undefined) data = data.filter((h) => h.bookId === bookId);
    if (chapter !== undefined) data = data.filter((h) => h.chapter === chapter);
    setHighlights(data.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
  }, [user, bookId, chapter]);

  useEffect(() => {
    fetchHighlights();
  }, [fetchHighlights]);

  const isHighlighted = useCallback(
    (bId: number, ch: number, vNum: number) =>
      highlights.some((h) => h.bookId === bId && h.chapter === ch && h.verseNum === vNum),
    [highlights]
  );

  const getHighlightColor = useCallback(
    (bId: number, ch: number, vNum: number) =>
      highlights.find((h) => h.bookId === bId && h.chapter === ch && h.verseNum === vNum)?.color ?? null,
    [highlights]
  );

  const toggleHighlight = useCallback(
    async (
      bId: number,
      bName: string,
      ch: number,
      vNum: number,
      vText: string,
      color: string,
      translation: string
    ) => {
      if (!user) return false;
      const all = getAllHighlights();
      const existingIdx = all.findIndex(
        (h) => h.userId === user.id && h.bookId === bId && h.chapter === ch && h.verseNum === vNum
      );
      if (existingIdx !== -1) {
        all.splice(existingIdx, 1);
        saveAllHighlights(all);
        fetchHighlights();
        return false;
      } else {
        const newH: Highlight & { userId: string } = {
          id: generateId(),
          userId: user.id,
          bookId: bId,
          bookName: bName,
          chapter: ch,
          verseNum: vNum,
          verseText: vText,
          color,
          translation,
          createdAt: new Date().toISOString(),
        };
        all.unshift(newH);
        saveAllHighlights(all);
        fetchHighlights();
        return true;
      }
    },
    [user, fetchHighlights]
  );

  return { highlights, loading, isHighlighted, getHighlightColor, toggleHighlight, refetch: fetchHighlights };
}
